import React, { useState } from 'react';
import api, { handleAPIError } from '../../services/api';

interface TopKResult {
  rank: number;
  title: string;
  category: string;
  rating: number | null;
  reviewCount: number;
  photosCount: number;
  isClaimed: boolean;
  is247: boolean;
  keywordInTitle: boolean;
  locationInTitle: boolean;
  address?: string;
  url?: string;
  phone?: string;
  [key: string]: unknown;
}

interface Benchmarks {
  topK: {
    avgRating: number;
    avgReviews: number;
    avgPhotos: number;
    claimedPct: number;
    count247: number;
  };
  topN: {
    avgRating: number;
    avgReviews: number;
    avgPhotos: number;
    claimedPct: number;
    count247: number;
  };
}

interface Recommendation {
  text: string;
  confidence: 'high' | 'medium' | 'low';
  category: string;
}

interface ClientMatch extends TopKResult {
  rank: number;
}

interface QueryReport {
  query: string;
  totalResults: number;
  topK: TopKResult[];
  benchmarks: Benchmarks;
  recommendations: Recommendation[];
  clientMatch: ClientMatch | null;
}

interface GMBAuditResponse {
  audit: Record<string, unknown>;
  queryReports: QueryReport[];
}

const GMBAudit: React.FC = () => {
  const [clientBusinessName, setClientBusinessName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [targetLocationName, setTargetLocationName] = useState('');
  const [targetQueries, setTargetQueries] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GMBAuditResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const location = targetLocationName.trim();
    const queriesText = targetQueries.trim();
    if (!location || !queriesText) return;

    const queries = queriesText
      .split('\n')
      .map((q) => q.trim())
      .filter((q) => q.length > 0);

    if (queries.length === 0) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post<GMBAuditResponse>('/seotools/gmb-audit', {
        client_business_name: clientBusinessName.trim() || undefined,
        client_address: clientAddress.trim() || undefined,
        target_location_name: location,
        target_queries: queries,
      });
      setResult(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const confidenceBadge = (confidence: string) => {
    const styles: Record<string, string> = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return (
      <span
        className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full border ${styles[confidence] || styles.low}`}
      >
        {confidence.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">GMB Audit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Audit Google Maps / Google Business Profile rankings for target queries in a location.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="gmb-business-name" className="block text-sm font-medium text-gray-700 mb-1">
              Client Business Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="gmb-business-name"
              type="text"
              value={clientBusinessName}
              onChange={(e) => setClientBusinessName(e.target.value)}
              placeholder="e.g. Joe's Plumbing LLC"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="gmb-client-address" className="block text-sm font-medium text-gray-700 mb-1">
              Client Address <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="gmb-client-address"
              type="text"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              placeholder="e.g. 123 Main St, Austin TX"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="gmb-location" className="block text-sm font-medium text-gray-700 mb-1">
            Target Location <span className="text-red-500">*</span>
          </label>
          <input
            id="gmb-location"
            type="text"
            value={targetLocationName}
            onChange={(e) => setTargetLocationName(e.target.value)}
            placeholder="e.g. Austin, Texas, United States"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            required
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="gmb-queries" className="block text-sm font-medium text-gray-700 mb-1">
            Target Queries <span className="text-red-500">*</span>
            <span className="text-gray-400 font-normal ml-1">(one per line)</span>
          </label>
          <textarea
            id="gmb-queries"
            value={targetQueries}
            onChange={(e) => setTargetQueries(e.target.value)}
            placeholder={"plumber near me\nemergency plumber\nplumbing repair service"}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
            required
            disabled={loading}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || !targetLocationName.trim() || !targetQueries.trim()}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Running Audit...
              </span>
            ) : (
              'Run GMB Audit'
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="space-y-8">
          <h3 className="text-lg font-semibold text-gray-800">
            Audit Results ({result.queryReports.length} {result.queryReports.length === 1 ? 'query' : 'queries'})
          </h3>

          {result.queryReports.map((report, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              {/* Query heading */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h4 className="text-base font-semibold text-gray-800">
                  Query: <span className="text-blue-600">"{report.query}"</span>
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  {report.totalResults} total results found
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Client match highlight */}
                {report.clientMatch && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h5 className="text-sm font-semibold text-green-800 mb-2">Client Found in Results</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-green-600 font-medium">Rank:</span>{' '}
                        <span className="font-bold text-green-900">#{report.clientMatch.rank}</span>
                      </div>
                      <div>
                        <span className="text-green-600 font-medium">Rating:</span>{' '}
                        {report.clientMatch.rating ?? 'N/A'}
                      </div>
                      <div>
                        <span className="text-green-600 font-medium">Reviews:</span>{' '}
                        {report.clientMatch.reviewCount}
                      </div>
                      <div>
                        <span className="text-green-600 font-medium">Photos:</span>{' '}
                        {report.clientMatch.photosCount}
                      </div>
                    </div>
                    <p className="text-sm text-green-700 mt-1 font-medium">{report.clientMatch.title}</p>
                  </div>
                )}

                {/* Benchmarks card */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">Benchmarks (Top K)</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">
                        {report.benchmarks.topK.avgRating.toFixed(1)}
                      </div>
                      <div className="text-xs text-blue-500 mt-1">Avg Rating</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-700">
                        {Math.round(report.benchmarks.topK.avgReviews)}
                      </div>
                      <div className="text-xs text-purple-500 mt-1">Avg Reviews</div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-indigo-700">
                        {Math.round(report.benchmarks.topK.avgPhotos)}
                      </div>
                      <div className="text-xs text-indigo-500 mt-1">Avg Photos</div>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-teal-700">
                        {report.benchmarks.topK.claimedPct.toFixed(0)}%
                      </div>
                      <div className="text-xs text-teal-500 mt-1">Claimed</div>
                    </div>
                  </div>
                </div>

                {/* Top results table */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">Top Results</h5>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-3 py-2 font-medium text-gray-600">#</th>
                          <th className="px-3 py-2 font-medium text-gray-600">Business</th>
                          <th className="px-3 py-2 font-medium text-gray-600">Category</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">Rating</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">Reviews</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">Photos</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">Claimed</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">24/7</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">KW in Title</th>
                          <th className="px-3 py-2 font-medium text-gray-600 text-center">Loc in Title</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {report.topK.map((item, i) => {
                          const isClient =
                            report.clientMatch && report.clientMatch.rank === item.rank;
                          return (
                            <tr
                              key={i}
                              className={isClient ? 'bg-green-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                            >
                              <td className="px-3 py-2 font-medium text-gray-700">{item.rank}</td>
                              <td className="px-3 py-2 text-gray-800 max-w-[200px] truncate" title={item.title}>
                                {isClient && (
                                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5" />
                                )}
                                {item.title}
                              </td>
                              <td className="px-3 py-2 text-gray-600 max-w-[150px] truncate" title={item.category}>
                                {item.category}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.rating != null ? (
                                  <span className="font-medium">{item.rating.toFixed(1)}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">{item.reviewCount}</td>
                              <td className="px-3 py-2 text-center">{item.photosCount}</td>
                              <td className="px-3 py-2 text-center">
                                {item.isClaimed ? (
                                  <span className="text-green-600 font-medium">Yes</span>
                                ) : (
                                  <span className="text-gray-400">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.is247 ? (
                                  <span className="text-blue-600 font-medium">Yes</span>
                                ) : (
                                  <span className="text-gray-400">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.keywordInTitle ? (
                                  <span className="text-green-600 font-medium">Yes</span>
                                ) : (
                                  <span className="text-gray-400">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.locationInTitle ? (
                                  <span className="text-green-600 font-medium">Yes</span>
                                ) : (
                                  <span className="text-gray-400">No</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recommendations */}
                {report.recommendations.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Recommendations</h5>
                    <ul className="space-y-2">
                      {report.recommendations.map((rec, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div className="mt-0.5 shrink-0">{confidenceBadge(rec.confidence)}</div>
                          <div>
                            <p className="text-sm text-gray-800">{rec.text}</p>
                            <p className="text-xs text-gray-500 mt-1">Category: {rec.category}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GMBAudit;
